import { Participant } from 'livekit-client';
import { participantsSortStore } from 'state/store';
import {
    selectFixedSlotsCount,
    selectParticipants,
    selectParticipantsSortOrder,
    selectRecentSpeakerId,
    selectRemoteParticipants,
} from 'state/selectors';
import {
    isWebinarEnabled,
    isLocalWebinarListener,
    isWebinarListenerParticipant,
    reorderForWebinarSpeakerFocus,
} from 'useCase/webinar/webinarStatus';
import { type ParticipantLikeObject } from './useInitialSort';

export const fixedSlotsCount = 9;

const lastSpeakingSorting = <T extends ParticipantLikeObject>(a: T, b: T) => {
    // В начале тот, кто говорил давно
    if (a.lastSpokeAt !== b.lastSpokeAt) {
        const aLast = a.lastSpokeAt?.getTime() ?? 0;
        const bLast = b.lastSpokeAt?.getTime() ?? 0;
        return aLast - bLast;
    }

    return 0;
};

/**
 * Свапает участников с поднятой рукой в видимую область (fixed slots).
 * Вытесняет участников с наименьшим приоритетом, начиная с конца fixed slots.
 * Порядок: первый поднявший руку отображается раньше второго.
 */
export const sortAfterHandRaised = (raisedHandParticipants: Participant[]) => {
    if (raisedHandParticipants.length === 0) return;

    const participants = selectParticipants();
    const sortOrder = selectParticipantsSortOrder();
    const ids = [...sortOrder];
    const fixedSlotsCount = selectFixedSlotsCount();
    const fixedIds = ids.slice(0, fixedSlotsCount);
    const fixedIdsSet = new Set(fixedIds);
    const raisedHandIds = new Set(raisedHandParticipants.map(({ identity }) => identity));

    // Участники с рукой, которых ещё нет в видимой области
    const raisedNotFixed = raisedHandParticipants.filter(({ identity }) => !fixedIdsSet.has(identity));

    if (raisedNotFixed.length === 0) return;

    // Слоты, доступные для замены: не локальный, не шарит экран, не говорит, не поднял руку
    const fixedParticipants = participants.filter(({ identity }) => fixedIdsSet.has(identity));
    const availableForSwap = fixedParticipants.filter(
        ({ isLocal, isScreenShareEnabled, isSpeaking, identity }) =>
            !isLocal && !isScreenShareEnabled && !isSpeaking && !raisedHandIds.has(identity),
    );

    if (availableForSwap.length === 0) return;

    // Заменяем с конца fixed slots (наименьший приоритет — последний)
    availableForSwap.sort((a, b) => fixedIds.indexOf(b.identity) - fixedIds.indexOf(a.identity));

    // Итерируем raisedNotFixed в обратном порядке: первый поднявший окажется на меньшем индексе
    const reversedRaised = [...raisedNotFixed].reverse();
    const swapCount = Math.min(availableForSwap.length, reversedRaised.length);

    for (let i = 0; i < swapCount; i++) {
        const idA = availableForSwap[i].identity;
        const posA = ids.indexOf(idA);
        const idB = reversedRaised[i].identity;
        const posB = ids.indexOf(idB);
        if (posB === -1) continue;
        ids[posA] = idB;
        ids[posB] = idA;
    }

    participantsSortStore.setSortOrder(ids);
};

export const useSort = () => {
    const getSortedParticipants = () => {
        const participants = selectParticipants();
        const sortOrder = selectParticipantsSortOrder();
        const participantsMap = new Map(participants.map((item) => [item.identity, item]));
        const sortedParticipants = sortOrder.reduce((acc, id) => {
            const participant = participantsMap.get(id);

            if (participant) {
                acc.push(participant);
                return acc;
            }

            return acc;
        }, [] as Participant[]);

        return sortedParticipants;
    };

    /**
     * Сортировка для грида и видов мобильный/в фокусе имеет небольшое отличие.
     * В случае, если спикер не я, то моя карточка должна быть на 2-м месте.
     * Исключение - если кто-то шарит экран.
     */
    const getSortedParticipantsWhenFocused = () => {
        const recentSpeakerId = selectRecentSpeakerId();
        let initialSortedParticipants = getSortedParticipants();

        if (isWebinarEnabled()) {
            initialSortedParticipants = reorderForWebinarSpeakerFocus(initialSortedParticipants, recentSpeakerId);
        }

        const fixedSlotsCount = selectFixedSlotsCount();
        const fixedParticipants = initialSortedParticipants.slice(0, fixedSlotsCount);

        // Если кто-то шарит экран - не менять сортировку
        const isSomeoneScreenSharing = fixedParticipants.some(({ isScreenShareEnabled }) => isScreenShareEnabled);

        if (isSomeoneScreenSharing) return initialSortedParticipants;

        // Недавно говоривший - первый
        const recentSpeaker = initialSortedParticipants.find(({ identity }) => identity === recentSpeakerId);

        if (!recentSpeaker) return initialSortedParticipants;

        const { identity: firstIdentity, isLocal } = recentSpeaker;

        // Если я и есть последний говоривший - ничего не делаем, я в окне спикера
        if (isLocal) {
            if (isWebinarListenerParticipant(recentSpeaker)) {
                return reorderForWebinarSpeakerFocus(initialSortedParticipants, recentSpeakerId);
            }
            return initialSortedParticipants;
        }

        const swappedMeToSecondSlotParticipants = [
            recentSpeaker,
            ...initialSortedParticipants.filter(({ identity }) => identity !== firstIdentity),
        ];

        return swappedMeToSecondSlotParticipants;
    };

    const setSortedParticipants = <T extends ParticipantLikeObject>(participants: T[]) => {
        const mapIdentityToIndex = participants.map(({ identity }) => identity);
        participantsSortStore.setSortOrder(mapIdentityToIndex);
    };

    const sortAfterSpeaking = <T extends ParticipantLikeObject>(speakers: T[]) => {
        const participants = selectParticipants();
        const participantIds = new Set(participants.map(({ identity }) => identity));
        // speakers может содержать уже отключённых участников
        const stillConnectedSpeakers = speakers.filter(({ identity }) => participantIds.has(identity));

        if (stillConnectedSpeakers.length === 0) return;

        const speakersForFocus = stillConnectedSpeakers.filter(
            (speaker) => !(speaker.isLocal && isLocalWebinarListener()),
        );

        if (speakersForFocus.length > 0) {
            findAndSwapOldestSpeakers(speakersForFocus, participants);
            participantsSortStore.setRecentSpeakerId(speakersForFocus[0].identity);
        }
    };

    const sortAfterScreenSharing = <T extends ParticipantLikeObject>(remoteParticipant: T) => {
        const remoteParticipants = selectRemoteParticipants();
        const participants = Array.from(remoteParticipants.values());
        findAndSwapOldestSpeakerWithScreenSharer(remoteParticipant, participants);
    };

    const findAndSwapOldestSpeakers = <T extends ParticipantLikeObject>(
        newSpeakers: T[],
        participants: Participant[],
    ) => {
        const sortOrder = selectParticipantsSortOrder();
        const ids = [...sortOrder];
        const fixedSlotsCount = selectFixedSlotsCount();
        const fixedsIds = ids.slice(0, fixedSlotsCount);
        const newSpeakersIds = newSpeakers.map(({ identity }) => identity);

        // Находим участников, которые уже зафиксированы
        const fixedIdsSet = new Set(fixedsIds);
        const diff = newSpeakersIds.filter((identity) => !fixedIdsSet.has(identity));

        // Если новые говорящие зафиксированы - ничего не делать
        if (diff.length === 0) return;

        // Ищем доступные для замены слоты среди фиксированных
        const fixedParticipants = participants.filter(({ identity }) => fixedIdsSet.has(identity));
        const availableForSwap = fixedParticipants.filter(
            ({ isLocal, isScreenShareEnabled, isSpeaking }) => !isLocal && !isScreenShareEnabled && !isSpeaking,
        );

        // Если доступных слотов нет - ничего не делать
        if (availableForSwap.length === 0) return;

        // Выбираем тех, кто дольше молчит
        availableForSwap.sort(lastSpeakingSorting);

        // Меняем местами с участником, который давно говорил
        for (let i = 0; i < availableForSwap.length; i++) {
            if (!diff[i]) break;

            const idA = availableForSwap[i].identity;
            const positionA = fixedsIds.findIndex((id) => id === idA);
            const idB = diff[i];
            const positionB = ids.findIndex((id) => id === idB);
            ids[positionA] = idB;
            ids[positionB] = idA;
        }

        // Устанавливаем новые id в стор
        participantsSortStore.setSortOrder(ids);
    };

    const findAndSwapOldestSpeakerWithScreenSharer = <T extends ParticipantLikeObject>(
        screenSharer: T,
        participants: Participant[],
    ) => {
        const sortOrder = selectParticipantsSortOrder();
        const ids = [...sortOrder];
        const fixedSlotsCount = selectFixedSlotsCount();
        const fixedsIds = ids.slice(0, fixedSlotsCount);

        // Ищем доступные для замены слоты среди фиксированных
        const fixedIdsSet = new Set(fixedsIds);
        const fixedParticipants = participants.filter(({ identity }) => fixedIdsSet.has(identity));
        const availableForSwap = fixedParticipants.filter(({ identity, isLocal, isScreenShareEnabled }) => {
            return !isLocal && !isScreenShareEnabled && identity !== screenSharer.identity;
        });

        // Если доступных слотов нет - ничего не делать
        if (availableForSwap.length === 0) return;

        // Первый по сортировке id из списка доступных
        const availableForSwapSet = new Set(availableForSwap.map(({ identity }) => identity));
        const idA = fixedsIds.find((id) => availableForSwapSet.has(id));
        if (!idA) return; // Невозможная ситуация, чтобы убрать ошибки TS

        const idB = screenSharer.identity;
        const positionA = fixedsIds.findIndex((id) => id === idA);
        const positionB = ids.findIndex((id) => id === idB);

        // Если тот кто шарит - уже в первых числах фикс слотов - ничего не делаем.
        if (positionB < positionA) return;

        // Меняем местами с участником, который фиксирован, но не шарит экран
        ids[positionA] = idB;
        ids[positionB] = idA;

        // Устанавливаем новые id в стор
        participantsSortStore.setSortOrder(ids);
    };

    const sortAfterConnect = <T extends ParticipantLikeObject>(connectedParticipant: T) => {
        const id = connectedParticipant.identity;
        const sortOrder = selectParticipantsSortOrder();

        if (!sortOrder.includes(id)) {
            participantsSortStore.pushId(id);
        }
    };

    const sortAfterDisconnect = <T extends ParticipantLikeObject>(disconnectedParticipant: T) => {
        const sortOrder = selectParticipantsSortOrder();
        const newSortedIds = sortOrder.filter((id) => id !== disconnectedParticipant.identity);

        participantsSortStore.setSortOrder(newSortedIds);

        // Для вида спикера ставим нового активного спикера
        const disconnectedId = disconnectedParticipant.identity;
        const recentSpeakerId = selectRecentSpeakerId();
        if (recentSpeakerId === disconnectedId) {
            participantsSortStore.setRecentSpeakerId(sortOrder[0]);
        }
    };

    return {
        getSortedParticipants,
        getSortedParticipantsWhenFocused,
        setSortedParticipants,
        sortAfterConnect,
        sortAfterDisconnect,
        sortAfterSpeaking,
        sortAfterScreenSharing,
        sortAfterHandRaised,
        findAndSwapOldestSpeakers,
        findAndSwapOldestSpeakerWithScreenSharer,
    };
};
